import React, { useState } from 'react';
import { Member, MemberRole } from '../types/types';

/* ── Bill type badge config ─────────────────────────────────────────────── */

const billTypeConfig: Record<string, { label: string; bg: string; color: string; description: string }> = {
  government: {
    label: 'הצעת חוק ממשלתית',
    bg: '#E6F1FB',
    color: '#0C447C',
    description: 'הצעת חוק שהוגשה ע"י הממשלה — סיכוי גבוה לעבור',
  },
  private: {
    label: 'הצעת חוק פרטית',
    bg: '#f5f5f5',
    color: '#666',
    description: 'הצעת חוק של חבר/ת כנסת בודד/ת — סיכוי נמוך יותר לעבור',
  },
  committee: {
    label: 'הצעת חוק ועדה',
    bg: '#FAEEDA',
    color: '#633806',
    description: 'הצעת חוק שעלתה מוועדה של הכנסת',
  },
};

/**
 * Determines the bill type badge based on the bill's type field
 * and optionally enriches it with sponsor role data.
 */
export const getBillTypeBadge = (billType: string, sponsor?: Member | null): {
  label: string;
  bg: string;
  color: string;
  description: string;
  isGovernment: boolean;
} => {
  // Primary signal: the bill's own type from OData
  const config = billTypeConfig[billType] || billTypeConfig.private;

  // If bill is private but sponsor has a government role, note it
  if (billType === 'private' && sponsor?.roles) {
    const hasGovRole = (sponsor.roles as MemberRole[]).some(r => r.category === 'government');
    if (hasGovRole) {
      return {
        ...config,
        label: 'הצעת חוק פרטית (שר/ה)',
        description: 'הצעת חוק פרטית שהוגשה ע"י שר/ה בממשלה',
        isGovernment: false,
      };
    }
  }

  return {
    ...config,
    isGovernment: billType === 'government',
  };
};

/* ── Component ──────────────────────────────────────────────────────────── */

interface BillTypeBadgeProps {
  billType: string;
  sponsor?: Member | null;
  size?: 'small' | 'normal';
  showTooltip?: boolean;
}

const BillTypeBadge = ({ billType, sponsor, size = 'normal', showTooltip = true }: BillTypeBadgeProps) => {
  const [showDesc, setShowDesc] = useState(false);
  const badge = getBillTypeBadge(billType, sponsor);
  const isSmall = size === 'small';

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onClick={showTooltip ? (e) => { e.stopPropagation(); setShowDesc(!showDesc); } : undefined}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontSize: isSmall ? '10px' : '11px',
          fontWeight: badge.isGovernment ? 600 : 500,
          padding: isSmall ? '2px 7px' : '3px 9px',
          borderRadius: '6px',
          background: badge.bg,
          color: badge.color,
          border: badge.isGovernment ? `1px solid ${badge.color}33` : 'none',
          cursor: showTooltip ? 'pointer' : 'default',
        }}
      >
        {badge.isGovernment && <span style={{ fontSize: isSmall ? '8px' : '10px' }}>⚖️</span>}
        {badge.label}
      </span>

      {/* Tooltip */}
      {showDesc && showTooltip && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: '4px',
          background: '#333', color: '#fff', fontSize: '11px',
          padding: '8px 12px', borderRadius: '8px', lineHeight: 1.5,
          whiteSpace: 'nowrap', zIndex: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          {badge.description}
        </div>
      )}
    </span>
  );
};

export default BillTypeBadge;
